import { useCallback, useState } from "react";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";
import { TravelCycleOutcomes } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCycleTravelsResult } from "@shared/interfaces/travel-cycle-analysis.interface";

import { TravelAnalysisFilter } from "../types";
import { userUtcOffset } from "./useTravelCycleAnalysis";

/** What the drill-down shows: the trucks of an outcome, or the failed travels of a truck. */
export interface CycleDrilldownView {
  /** Outcome listed, or, for a truck, the outcome to highlight. */
  outcome?: TravelCycleOutcomes;
  idVehicle?: string;
  /** The truck was opened from an outcome, so there is a way back to it. */
  fromOutcome?: boolean;
}

/** Return shape of the `useTravelCycleDrilldown` hook. */
interface UseTravelCycleDrilldownReturn {
  open: boolean;
  view: CycleDrilldownView | null;
  data: TravelCycleTravelsResult | null;
  loading: boolean;
  openOutcome: (outcome: TravelCycleOutcomes) => void;
  openVehicle: (idVehicle: string, outcome?: TravelCycleOutcomes, fromOutcome?: boolean) => void;
  close: () => void;
}

/**
 * Drill-down of the production cycle: outcome › trucks › travels. Each step reads
 * `POST travels/analysis/cycle/travels` with the screen filter.
 * @param filter Current filter of the screen.
 */
const useTravelCycleDrilldown = (filter: TravelAnalysisFilter | null): UseTravelCycleDrilldownReturn => {

  const { addToast } = useToast();
  const { t } = useTranslation();
  const [view, setView] = useState<CycleDrilldownView | null>(null);
  const [data, setData] = useState<TravelCycleTravelsResult | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (next: CycleDrilldownView) => {
    setView(next);
    setData(null);
    setLoading(true);

    try {
      const { data: response } = await api.post("travels/analysis/cycle/travels", {
        ...(filter || {}),
        utcOffset: userUtcOffset(),
        // A truck shows all its failures; the outcome it was opened from is only highlighted
        outcome: next.idVehicle ? undefined : next.outcome,
        idVehicle: next.idVehicle,
        onlyFailures: !!next.idVehicle || !next.outcome
      });

      if (response.status === "success") setData(response.result);
      else addToast({ type: "info", title: t(ToastMessages.alert), description: response.message });
    } catch (err) {
      addToast({
        type: "error",
        title: t(ToastMessages.error),
        description: err?.response?.data?.message || t(ToastMessages.connectionNotEstablished)
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openOutcome = useCallback((outcome: TravelCycleOutcomes) => load({ outcome }), [load]);

  const openVehicle = useCallback((idVehicle: string, outcome?: TravelCycleOutcomes, fromOutcome = false) => (
    load({ idVehicle, outcome, fromOutcome })
  ), [load]);

  const close = useCallback(() => {
    setView(null);
    setData(null);
  }, []);

  return { open: !!view, view, data, loading, openOutcome, openVehicle, close };
};

export default useTravelCycleDrilldown;
